import React from 'react';
import * as Styles from './HighlightFeatures.styles';
import useLocale from '@/hooks/useLocale';
import { useResize } from '@/hooks/useResize';
import withErrorBoundary from '@/utils/errorBoundary';
import CardFeatures from './CardFeatures/CardFeatures';
import imgLogoBuilder from '../../media/highlight-features/logo-builder.png';
import imgLogoBuilderMobile from '../../media/highlight-features/logo-builder-mobile.png';
import imgBackgroundRemover from '../../media/highlight-features/background-remover.png';
import imgBrandKit from '../../media/highlight-features/brand-kit.png';
import imgBrandKitMobile from '../../media/highlight-features/brand-kit-mobile.png';
import {
  CREDITS_TO_ACTIVE_FEATURE, ID_PRO_FEATURES, IS_PRO_FEATURE, BREAKPOINTS,
} from '../../studio.enum';
import ButtonCreateSiteAi from './ButtonCreateSiteAi/ButtonCreateSiteAi';
import { studioAnalytics } from '@/analytics/studio';
import { IconStars } from 'gatorcomponents';
import { useStudio } from '@/hooks/studio';

const HighlightFeatures = ({
  isProPlan, plan, credits, setModalFeatureProToShow, setShowModalLogoGeneration,
}) => {
  const { studio: studioLocale } = useLocale();
  const { openStudio } = useStudio();
  const windowRect = useResize();
  const { width } = windowRect;
  const mobile = width < BREAKPOINTS.MOBILE;
  const smallDesktop = width < BREAKPOINTS.SMALL_DESKTOP;

  const backgroundRemoverCtaAnalytics = () => {
    if (!isProPlan) {
      studioAnalytics.backgroundRemoverStudioFreeCta();
      return;
    }

    if (isProPlan && credits < CREDITS_TO_ACTIVE_FEATURE.BACKGROUND_REMOVER) {
      studioAnalytics.backgroundRemoverNoCreditsCta();
      return;
    }

    studioAnalytics.removeBackgroundClick();
  };

  const brandKitCtaAnalytics = () => {
    if (!isProPlan) {
      studioAnalytics.brandKitStudioFreeCta();
      return;
    }

    if (isProPlan && credits < CREDITS_TO_ACTIVE_FEATURE.BRAND_KIT) {
      studioAnalytics.brandKitNoCreditsCta();
      return;
    }

    studioAnalytics.brandKitClick();
  };

  const logoBuilderCtaAnalytics = () => {
    if (credits < CREDITS_TO_ACTIVE_FEATURE.LOGO_BUILDER) {
      studioAnalytics.logoBuilderNoCreditsClick();
      return;
    }

    studioAnalytics.logoCreatorClick();
  };

  const getCreditsText = (credits) => {
    if (credits === 1) {
      return `${studioLocale.highlightFeatures.tag.credits.prefix} 1 ${studioLocale.highlightFeatures.tag.credits.singularSuffix}`;
    }
    return `${studioLocale.highlightFeatures.tag.credits.prefix} ${credits} ${studioLocale.highlightFeatures.tag.credits.pluralSuffix}`;
  };

  const FeaturesCards = [
    {
      title: studioLocale.highlightFeatures.logoBuilder.title,
      description: studioLocale.highlightFeatures.logoBuilder.description,
      img: !smallDesktop ? imgLogoBuilder : imgLogoBuilderMobile,
      imgPosition: 'left',
      showTag: IS_PRO_FEATURE.LOGO_BUILDER,
      id: ID_PRO_FEATURES.LOGO_BUILDER,
      creditsText: getCreditsText(CREDITS_TO_ACTIVE_FEATURE.LOGO_BUILDER),
      event: () => logoBuilderCtaAnalytics(),
      action: () => setShowModalLogoGeneration(true),
    },
    {
      title: studioLocale.highlightFeatures.backgroundRemover.title,
      description: studioLocale.highlightFeatures.backgroundRemover.description,
      img: imgBackgroundRemover,
      imgPosition: smallDesktop ? 'center' : 'left',
      showTag: IS_PRO_FEATURE.BACKGROUND_REMOVER,
      id: ID_PRO_FEATURES.BACKGROUND_REMOVER,
      creditsText: getCreditsText(CREDITS_TO_ACTIVE_FEATURE.BACKGROUND_REMOVER),
      event: () => backgroundRemoverCtaAnalytics(),
      action: () => openStudio('background_remover'),
    },
    {
      title: studioLocale.highlightFeatures.brandKit.title,
      description: studioLocale.highlightFeatures.brandKit.description,
      img: !smallDesktop ? imgBrandKit : imgBrandKitMobile,
      imgPosition: 'left',
      showTag: IS_PRO_FEATURE.BRAND_KIT,
      id: ID_PRO_FEATURES.BRAND_KIT,
      creditsText: getCreditsText(CREDITS_TO_ACTIVE_FEATURE.BRAND_KIT),
      event: () => brandKitCtaAnalytics(),
      action: () => openStudio('brand_kit'),
    },
  ];

  const handleShowModalNoCredits = () => {
    setModalFeatureProToShow(ID_PRO_FEATURES.NO_CREDITS);
  };

  const handleShowModalByFeature = (feature) => {
    setModalFeatureProToShow(feature);
  };

  const handleActionFeature = (featureId) => {
    const feature = FeaturesCards.find(card => card.id === featureId);

    if (feature && feature.action) {
      feature.action();
    }
  };

  const handleFreeFlow = (feature) => {
    !IS_PRO_FEATURE[feature] && (credits >= CREDITS_TO_ACTIVE_FEATURE[feature])
      ? handleActionFeature(feature)
      : handleShowModalNoCredits();

    IS_PRO_FEATURE[feature] && handleShowModalByFeature(feature);
  };

  const handleProFlow = (feature) => {
    credits >= CREDITS_TO_ACTIVE_FEATURE[feature]
      ? handleActionFeature(feature)
      : handleShowModalNoCredits();
  };

  const handleClickCardFeature = (featureId) => {
    const feature = FeaturesCards.find(card => card.id === featureId);

    feature.event();

    isProPlan ? handleProFlow(featureId) : handleFreeFlow(featureId);
  };

  return (
    <Styles.Wrapper isProPlan={isProPlan}>
      <Styles.Header>
        <Styles.DivContentHeader>
          <IconStars color="primaryBlue" />
          <Styles.Title>
            {studioLocale.highlightFeatures.header.titleFeatures}
          </Styles.Title>
        </Styles.DivContentHeader>

        {!mobile && isProPlan && (
          <ButtonCreateSiteAi position="top" />
        )}
      </Styles.Header>
      <Styles.CardsWrapper>
        {FeaturesCards.map(card => (
          <CardFeatures
            key={card.title}
            CardTitle={card.title}
            CardDescription={card.description}
            Img={card.img}
            imgPosition={card.imgPosition}
            showTag={card.showTag}
            creditsText={card.creditsText}
            isProPlan={isProPlan}
            plan={plan}
            credits={credits}
            onClick={() => handleClickCardFeature(card.id)}
          />
        ))}
      </Styles.CardsWrapper>
    </Styles.Wrapper>
  );
};
export default withErrorBoundary(HighlightFeatures, 'HighlightFeatures');
